import React, {Fragment, useMemo} from 'react';
import {TablePropsInterface} from "@/views/Admin/Events/Event/Promocodes/Table/Table.props.interface";
import styles from "./Table.module.scss";
import {Checkbox, Preloader} from "uikit";
import Promocode from "@/views/Admin/Events/Event/Promocodes/Promocode/Promocode";
import SortIcon from "@/assets/images/icons/12/sort.svg";
import {useQueryRouter} from "@/hooks/useQueryRouter/useQueryRouter";
import Group from "@/views/Admin/Events/Event/Promocodes/Group/Group";
import {useLocation} from "react-router-dom";
import {PromocodeGroupsServiceInterface} from "@/services/PromocodeGroupsService/PromocodeGroupsService.interface";
import {PromocodesServiceInterface} from "@/services/PromocodesService/PromocodesService.interface";

type ResourceType = PromocodeGroupsServiceInterface | PromocodesServiceInterface

const Table = ({data, onChange, onRemove, onEdit}: TablePropsInterface) => {
    const {promocodes, groups, isLoading} = data
    const {query, set} = useQueryRouter()
    const {search} = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const tabs = data.tabs
    const content = tabs.includes(params.get('content') as string) ? params.get('content') : 'promocodes';
    const current = data[content]
    const isFilterActive = Object.values(query.filter ?? {}).some(v => v !== '' && v !== null && v !== undefined);
    const isNotFound = Boolean(!current.data.length && isFilterActive);
    const isActive = Boolean(current.data.length && current.data.every(p => p.isActive))

    const handleActiveGlobal = () => {
        onChange(prev => ({
            ...prev,
            [content]: {
                ...prev[content],
                data: prev[content].data.map(p => ({...p, isActive: !isActive}))
            },
        }))
    }

    const handleActiveResource = (data: ResourceType) => {
        onChange(prev => ({
            ...prev,
            [content]: {
                ...prev[content],
                data: prev[content].data.map(p =>
                    p.hash === data.hash ? {...p, isActive: !p.isActive} : p
                )
            },
        }));
    }

    const handleToggleSort = (field: string) => {
        const list = (query.sort || '').split(',').filter(Boolean);
        const ascIndex = list.indexOf(field);
        const descIndex = list.indexOf(`-${field}`)

        if (ascIndex !== -1) {
            list.splice(ascIndex, 1, `-${field}`)
        } else if (descIndex !== -1) {
            list.splice(descIndex, 1)
        } else {
            list.unshift(field);
        }

        set({sort: list.length ? list.join(',') : null});
    };

    const getSortDirection = (field: string): 'asc' | 'desc' | null => {
        const sortList = (query.sort || '').split(',').filter(Boolean);
        if (sortList.includes(field)) return 'asc';
        if (sortList.includes(`-${field}`)) return 'desc';
        return null;
    };

    const renderSortIcon = (field: string) => (
        <SortIcon className={[
            styles.sort,
            getSortDirection(field) && styles.isActive,
            getSortDirection(field) === 'desc' && styles.isDesc,
        ].filter(Boolean).join(' ')}/>
    );

    const handleEditResource = async (data: ResourceType) => {
        onChange(prev => ({...prev, isLoading: true, [content]: {...prev[content], isLoading: true}}));

        const response = await onEdit(data.id);

        const promocode = content === 'promocodes'
            ? {...response.data, isEdit: true}
            : {...(response.data?.promocodes?.[0] || {}), isEdit: true};

        const group = content === 'promocodes'
            ? {...response.data?.promocodeGroup}
            : {...response.data, isEdit: true};

        onChange(prev => ({
            ...prev,
            isLoading: false,
            promocodes: {
                ...prev.promocodes,
                isLoading: false,
                data: data.hash && promocodes.data.some(p => p.id === promocode.id)
                    ? prev.promocodes.data.map(p => p.id === promocode.id ? ({...p, ...promocode}) : p)
                    : [data.hash ? promocode : {...promocode, id: null, promocode: null}, ...prev.promocodes.data]
            },
            groups: {
                ...prev.groups,
                isLoading: false,
                data: groups.data.some(g => g.id === group.id)
                    ? prev.groups.data.map(g => g.id === group.id ? ({...g, ...group}) : g)
                    : [group, ...prev.groups.data].filter(Boolean)
            }
        }))
    };

    return (
        <div className={styles.container}>
            {!isNotFound && (
                <div className={[
                    styles.table,
                    styles[content],
                    !current.data.length && styles.isEmpty
                ].filter(Boolean).join(' ')}>
                    <div className={[styles.row, styles.header].filter(Boolean).join(' ')}>
                        {content === 'promocodes' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('promocode')}>
                                <span onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        value={!!isActive}
                                        disabled={isLoading}
                                        onChange={handleActiveGlobal}
                                    />
                                </span>
                                <p>
                                    Промокод{renderSortIcon('promocode')}
                                </p>
                            </div>}

                        {content === 'promocodes' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('groupName')}>
                                <p>
                                    Группа
                                    {renderSortIcon('groupName')}
                                </p>
                            </div>}

                        {content === 'groups' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('name')}>
                                <span onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        value={!!isActive}
                                        disabled={isLoading}
                                        onChange={handleActiveGlobal}
                                    />
                                </span>
                                <p>
                                    Группа{renderSortIcon('promocode')}
                                </p>
                            </div>}

                        <div className={styles.cell} onClick={() => handleToggleSort('eventName')}>
                            <p>
                                События
                                {renderSortIcon('eventName')}
                            </p>
                        </div>

                        {content === 'promocodes' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('count')}>
                                <p>
                                    Использовано
                                    {renderSortIcon('count')}
                                </p>
                            </div>}

                        {content === 'groups' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('count')}>
                                <p>
                                    Количество
                                    {renderSortIcon('count')}
                                </p>
                            </div>}

                        {content === 'promocodes' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('from')}>
                                <p>
                                    Период
                                    {renderSortIcon('from')}
                                </p>
                            </div>}

                        {content === 'promocodes' &&
                            <div className={styles.cell} onClick={() => handleToggleSort('size')}>
                                <p>
                                    Скидка
                                    {renderSortIcon('size')}
                                </p>
                            </div>}

                        <div className={styles.cell}/>
                    </div>

                    <div className={[styles.body, isLoading && styles.isLoading].filter(Boolean).join(' ')}>
                        {isLoading && <Preloader className={styles.preloader}/>}

                        {content === 'promocodes' && promocodes.data.map(p => (
                            <Fragment key={p.hash}>
                                <Promocode
                                    data={p}
                                    onChange={onChange}
                                    onRemove={onRemove}
                                    onEdit={handleEditResource}
                                    onActive={handleActiveResource}
                                />
                            </Fragment>
                        ))}

                        {content === 'groups' && groups.data.map(g => (
                            <Fragment key={g.hash}>
                                <Group
                                    data={g}
                                    onChange={onChange}
                                    onRemove={onRemove}
                                    onEdit={handleEditResource}
                                    onActive={handleActiveResource}
                                />
                            </Fragment>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Table;
